import type { AdmissionsAssistantArtifactPreview } from "./types";
import { TUITION_PRIOR_YEAR_VISUALIZATION_PROMPT } from "../../shared/assistant-visualization-prompts";

export type AssistantArtifactPromptSource =
  | "task_intent"
  | "promoted_suggestion"
  | "component_quick_action"
  | "config_tool_registry"
  | "report_workspace"
  | "runtime_artifact";

export type AssistantArtifactFamily =
  | "spreadsheet"
  | "chart"
  | "visualization3d"
  | "landing_page"
  | "diagram"
  | "report"
  | "revenue_report"
  | "invoice";

export type AssistantArtifactLaunchMode =
  | "auto_start"
  | "task_proposal"
  | "requires_active_workbook"
  | "workspace_action"
  | "requires_confirmation"
  | "ui_preview";

export type AssistantArtifactPromptRenderContractCase = {
  caseId: string;
  promptSource: AssistantArtifactPromptSource;
  prompt: string;
  route: string;
  expectedWorkflowKey: string;
  taskClass:
    | "artifact_generate"
    | "compute_report"
    | "draft_then_approve"
    | "external_side_effect"
    | null;
  launchMode: AssistantArtifactLaunchMode;
  artifactFamily: AssistantArtifactFamily;
  requiredPayloadFields: string[];
  requiredRenderTestId: string;
  rawContentDenylist: string[];
  artifactProducingOnly?: boolean;
};

const RAW_ARTIFACT_CONTENT_DENYLIST = [
  "```",
  "export function",
  "<pre",
  "<code",
  "<!doctype html",
  "<html",
  "<script",
  "sourceRef",
  "source_ref",
  "runId",
  "workItemId",
];

export const ASSISTANT_ARTIFACT_PROMPT_RENDER_CONTRACT_CASES = [
  {
    caseId: "task-intent-spreadsheet-generate",
    promptSource: "task_intent",
    prompt: "Generate a spreadsheet export for the current route.",
    route: "/school/admissions/dashboard",
    expectedWorkflowKey: "artifact.spreadsheet.generate",
    taskClass: "artifact_generate",
    launchMode: "auto_start",
    artifactFamily: "spreadsheet",
    requiredPayloadFields: [
      "workbookArtifactRef",
      "downloadArtifactRef",
      "workbookPreview",
    ],
    requiredRenderTestId: "ai-workbook-inline-preview",
    rawContentDenylist: RAW_ARTIFACT_CONTENT_DENYLIST,
  },
  {
    caseId: "task-intent-spreadsheet-modify",
    promptSource: "task_intent",
    prompt: "Show only scheduled applicants and remove the Family column.",
    route: "/school/admissions/dashboard",
    expectedWorkflowKey: "artifact.spreadsheet.modify",
    taskClass: "artifact_generate",
    launchMode: "requires_active_workbook",
    artifactFamily: "spreadsheet",
    requiredPayloadFields: [
      "workbookArtifactRef",
      "downloadArtifactRef",
      "workbookPreview",
    ],
    requiredRenderTestId: "ai-workbook-inline-preview",
    rawContentDenylist: RAW_ARTIFACT_CONTENT_DENYLIST,
  },
  {
    caseId: "task-intent-chart-generate",
    promptSource: "task_intent",
    prompt:
      "Make a simple bar chart categorized by status using a Recharts diagram.",
    route: "/school/admissions/dashboard",
    expectedWorkflowKey: "artifact.chart.generate",
    taskClass: "artifact_generate",
    launchMode: "auto_start",
    artifactFamily: "chart",
    requiredPayloadFields: ["chartArtifactRef", "chartPanels"],
    requiredRenderTestId: "ai-chart-inline-preview",
    rawContentDenylist: RAW_ARTIFACT_CONTENT_DENYLIST,
  },
  {
    caseId: "task-intent-visualization3d-generate",
    promptSource: "task_intent",
    prompt:
      "generate an advanced visualization of all admissions data using threejs",
    route: "/school/admissions/dashboard",
    expectedWorkflowKey: "artifact.visualization3d.generate",
    taskClass: "artifact_generate",
    launchMode: "auto_start",
    artifactFamily: "visualization3d",
    requiredPayloadFields: [
      "visualization3DArtifactRef",
      "visualization3DSpace",
      "visualization3DPanels",
    ],
    requiredRenderTestId: "ai-visualization3d-inline-preview",
    rawContentDenylist: RAW_ARTIFACT_CONTENT_DENYLIST,
  },
  {
    caseId: "task-intent-tuition-prior-year-visualization3d-generate",
    promptSource: "task_intent",
    prompt: TUITION_PRIOR_YEAR_VISUALIZATION_PROMPT,
    route: "/school/tuition",
    expectedWorkflowKey: "artifact.visualization3d.generate",
    taskClass: "artifact_generate",
    launchMode: "auto_start",
    artifactFamily: "visualization3d",
    requiredPayloadFields: [
      "visualization3DArtifactRef",
      "visualization3DSpace",
      "visualization3DPanels",
    ],
    requiredRenderTestId: "ai-visualization3d-inline-preview",
    rawContentDenylist: RAW_ARTIFACT_CONTENT_DENYLIST,
  },
  {
    caseId: "task-intent-landing-page-draft",
    promptSource: "task_intent",
    prompt: "Draft a landing page for the current route context.",
    route: "/school/settings/general",
    expectedWorkflowKey: "artifact.landing_page.draft",
    taskClass: "artifact_generate",
    launchMode: "task_proposal",
    artifactFamily: "landing_page",
    requiredPayloadFields: ["landingPageArtifactRef", "landingPagePreview"],
    requiredRenderTestId: "ai-landing-page-inline-preview",
    rawContentDenylist: RAW_ARTIFACT_CONTENT_DENYLIST,
  },
  {
    caseId: "task-intent-diagram-generate",
    promptSource: "task_intent",
    prompt: "Generate a Mermaid diagram of the admissions process.",
    route: "/school/admissions/dashboard",
    expectedWorkflowKey: "artifact.diagram.generate",
    taskClass: "artifact_generate",
    launchMode: "task_proposal",
    artifactFamily: "diagram",
    requiredPayloadFields: ["diagramArtifactRef", "diagramPreview"],
    requiredRenderTestId: "ai-diagram-inline-preview",
    rawContentDenylist: RAW_ARTIFACT_CONTENT_DENYLIST,
  },
  {
    caseId: "promoted-suggestion-visualization3d",
    promptSource: "promoted_suggestion",
    prompt:
      "generate an advanced visualization of all admissions data using threejs",
    route: "/school/admissions/dashboard",
    expectedWorkflowKey: "artifact.visualization3d.generate",
    taskClass: "artifact_generate",
    launchMode: "auto_start",
    artifactFamily: "visualization3d",
    requiredPayloadFields: [
      "visualization3DArtifactRef",
      "visualization3DSpace",
      "visualization3DPanels",
    ],
    requiredRenderTestId: "ai-visualization3d-inline-preview",
    rawContentDenylist: RAW_ARTIFACT_CONTENT_DENYLIST,
  },
  {
    caseId: "promoted-suggestion-chart",
    promptSource: "promoted_suggestion",
    prompt:
      "Make a simple bar chart categorized by status using a Recharts diagram.",
    route: "/school/admissions/dashboard",
    expectedWorkflowKey: "artifact.chart.generate",
    taskClass: "artifact_generate",
    launchMode: "auto_start",
    artifactFamily: "chart",
    requiredPayloadFields: ["chartArtifactRef", "chartPanels"],
    requiredRenderTestId: "ai-chart-inline-preview",
    rawContentDenylist: RAW_ARTIFACT_CONTENT_DENYLIST,
  },
  {
    caseId: "promoted-suggestion-spreadsheet",
    promptSource: "promoted_suggestion",
    prompt: "Generate a spreadsheet export for the current route.",
    route: "/school/admissions/dashboard",
    expectedWorkflowKey: "artifact.spreadsheet.generate",
    taskClass: "artifact_generate",
    launchMode: "auto_start",
    artifactFamily: "spreadsheet",
    requiredPayloadFields: [
      "workbookArtifactRef",
      "downloadArtifactRef",
      "workbookPreview",
    ],
    requiredRenderTestId: "ai-workbook-inline-preview",
    rawContentDenylist: RAW_ARTIFACT_CONTENT_DENYLIST,
  },
  {
    caseId: "component-quick-action-visualization3d",
    promptSource: "component_quick_action",
    prompt:
      "generate an advanced visualization of all admissions data using threejs",
    route: "/school/admissions/dashboard",
    expectedWorkflowKey: "artifact.visualization3d.generate",
    taskClass: "artifact_generate",
    launchMode: "auto_start",
    artifactFamily: "visualization3d",
    requiredPayloadFields: [
      "visualization3DArtifactRef",
      "visualization3DSpace",
      "visualization3DPanels",
    ],
    requiredRenderTestId: "ai-visualization3d-inline-preview",
    rawContentDenylist: RAW_ARTIFACT_CONTENT_DENYLIST,
  },
  {
    caseId: "component-quick-action-spreadsheet",
    promptSource: "component_quick_action",
    prompt: "Generate a spreadsheet export for the current route.",
    route: "/school/admissions/dashboard",
    expectedWorkflowKey: "artifact.spreadsheet.generate",
    taskClass: "artifact_generate",
    launchMode: "auto_start",
    artifactFamily: "spreadsheet",
    requiredPayloadFields: [
      "workbookArtifactRef",
      "downloadArtifactRef",
      "workbookPreview",
    ],
    requiredRenderTestId: "ai-workbook-inline-preview",
    rawContentDenylist: RAW_ARTIFACT_CONTENT_DENYLIST,
  },
  {
    caseId: "component-quick-action-chart",
    promptSource: "component_quick_action",
    prompt:
      "Make a simple bar chart categorized by status using a Recharts diagram.",
    route: "/school/admissions/dashboard",
    expectedWorkflowKey: "artifact.chart.generate",
    taskClass: "artifact_generate",
    launchMode: "auto_start",
    artifactFamily: "chart",
    requiredPayloadFields: ["chartArtifactRef", "chartPanels"],
    requiredRenderTestId: "ai-chart-inline-preview",
    rawContentDenylist: RAW_ARTIFACT_CONTENT_DENYLIST,
  },
  {
    caseId: "config-tool-diagram",
    promptSource: "config_tool_registry",
    prompt: "Generate a Mermaid diagram for the Admissions workflow.",
    route: "/school/admissions/dashboard",
    expectedWorkflowKey: "artifact.diagram.generate",
    taskClass: "artifact_generate",
    launchMode: "task_proposal",
    artifactFamily: "diagram",
    requiredPayloadFields: ["diagramArtifactRef", "diagramPreview"],
    requiredRenderTestId: "ai-diagram-inline-preview",
    rawContentDenylist: RAW_ARTIFACT_CONTENT_DENYLIST,
  },
  {
    caseId: "config-tool-landing-page",
    promptSource: "config_tool_registry",
    prompt: "Draft a landing page for the current route context.",
    route: "/school/settings/general",
    expectedWorkflowKey: "artifact.landing_page.draft",
    taskClass: "artifact_generate",
    launchMode: "task_proposal",
    artifactFamily: "landing_page",
    requiredPayloadFields: ["landingPageArtifactRef", "landingPagePreview"],
    requiredRenderTestId: "ai-landing-page-inline-preview",
    rawContentDenylist: RAW_ARTIFACT_CONTENT_DENYLIST,
  },
  {
    caseId: "report-workspace-generate",
    promptSource: "report_workspace",
    prompt: "Generate the selected report template.",
    route: "/school/reports/run",
    expectedWorkflowKey: "assistant.report.generate",
    taskClass: "compute_report",
    launchMode: "workspace_action",
    artifactFamily: "report",
    requiredPayloadFields: ["artifactRefs"],
    requiredRenderTestId: "ai-artifact-card-list",
    rawContentDenylist: RAW_ARTIFACT_CONTENT_DENYLIST,
    artifactProducingOnly: true,
  },
  {
    caseId: "task-intent-pending-payment-revenue-report",
    promptSource: "task_intent",
    prompt: "Build a pending-payment revenue report for the current route.",
    route: "/school/students/dashboard",
    expectedWorkflowKey: "finance.report.pending_payment_revenue",
    taskClass: "compute_report",
    launchMode: "task_proposal",
    artifactFamily: "revenue_report",
    requiredPayloadFields: ["artifactRefs"],
    requiredRenderTestId: "ai-artifact-card-list",
    rawContentDenylist: RAW_ARTIFACT_CONTENT_DENYLIST,
    artifactProducingOnly: true,
  },
  {
    caseId: "task-intent-invoice-preview",
    promptSource: "task_intent",
    prompt: "Send an invoice to this family.",
    route: "/school/students/family/fam-123/tuition",
    expectedWorkflowKey: "finance.invoice.send",
    taskClass: "external_side_effect",
    launchMode: "requires_confirmation",
    artifactFamily: "invoice",
    requiredPayloadFields: ["artifactRefs"],
    requiredRenderTestId: "ai-artifact-card-list",
    rawContentDenylist: RAW_ARTIFACT_CONTENT_DENYLIST,
    artifactProducingOnly: true,
  },
] satisfies AssistantArtifactPromptRenderContractCase[];

export const ASSISTANT_ARTIFACT_PROMPT_RENDER_WORKFLOW_KEYS = Array.from(
  new Set(
    ASSISTANT_ARTIFACT_PROMPT_RENDER_CONTRACT_CASES.map(
      (entry) => entry.expectedWorkflowKey,
    ),
  ),
).sort();

export const ARTIFACT_PROMPT_RENDER_CONTRACT_BY_WORKFLOW = new Map(
  ASSISTANT_ARTIFACT_PROMPT_RENDER_CONTRACT_CASES.map(
    (entry) => [entry.expectedWorkflowKey, entry] as const,
  ),
);

export const listArtifactPromptRenderContractCasesForWorkflow = (
  workflowKey: string | null | undefined,
): AssistantArtifactPromptRenderContractCase[] => {
  const normalized = String(workflowKey ?? "").trim();
  return ASSISTANT_ARTIFACT_PROMPT_RENDER_CONTRACT_CASES.filter(
    (entry) => entry.expectedWorkflowKey === normalized,
  );
};

export const resolveArtifactFamilyForWorkflow = (
  workflowKey: string | null | undefined,
): AssistantArtifactFamily | null =>
  ARTIFACT_PROMPT_RENDER_CONTRACT_BY_WORKFLOW.get(
    String(workflowKey ?? "").trim(),
  )?.artifactFamily ?? null;

export const isContractedArtifactWorkflow = (
  workflowKey: string | null | undefined,
): boolean =>
  ARTIFACT_PROMPT_RENDER_CONTRACT_BY_WORKFLOW.has(
    String(workflowKey ?? "").trim(),
  );

export const requiresArtifactPreviewContract = (input: {
  workflowKey?: string | null;
  taskClass?: string | null;
}): boolean => {
  const workflowKey = String(input.workflowKey ?? "").trim();
  if (isContractedArtifactWorkflow(workflowKey)) {
    return true;
  }
  return (
    workflowKey.startsWith("artifact.") ||
    String(input.taskClass ?? "").trim() === "artifact_generate"
  );
};

export const hasRequiredArtifactPreviewForWorkflow = (input: {
  workflowKey?: string | null;
  artifactPreview?: AdmissionsAssistantArtifactPreview | null;
}): boolean => {
  const workflowKey = String(input.workflowKey ?? "").trim();
  const family = resolveArtifactFamilyForWorkflow(workflowKey);
  const preview = input.artifactPreview ?? null;
  if (!family) {
    return !requiresArtifactPreviewContract({ workflowKey });
  }
  if (!preview) {
    return false;
  }
  switch (family) {
    case "spreadsheet":
      return Boolean(preview.workbookArtifact && preview.workbookPreview);
    case "chart":
      return Boolean(
        preview.chartArtifact && (preview.chartPanels?.length ?? 0) > 0,
      );
    case "visualization3d":
      return Boolean(
        preview.visualization3DArtifact &&
          preview.visualization3DSpace &&
          (preview.visualization3DPanels?.length ?? 0) > 0,
      );
    case "landing_page":
      return Boolean(
        preview.landingPageArtifact && preview.landingPagePreview?.previewHtml,
      );
    case "diagram":
      return Boolean(
        preview.diagramArtifact && preview.diagramPreview?.mermaidSource,
      );
    case "report":
    case "revenue_report":
    case "invoice":
      return (preview.artifactCards?.length ?? 0) > 0;
    default:
      return false;
  }
};
